import React, {useCallback, useState} from 'react';
import styled from "styled-components";
import {useWeb3React} from "@web3-react/core";
import BigNumber from "bignumber.js";
import {BIG_ZERO, correctToFixed, getBalanceNumber, getFullDisplayBalance} from "../utils/formatBalance";
import Button from "./Button/Button";
import {ButtonAppearanceEnum} from "./Button/types";
import {useFarmFromPid} from "../state/hooks";
import DepositModalContract from "./DepositModalContract";
import WithdrawModalContract from "./WithdrawModalContract";
import depositImg from "../assets/buttons/deposit.svg"
import withdrawImg from "../assets/buttons/withdraw.svg"

interface DepositCardWrapperProps {
    noAccount: boolean
}
const DepositCardWrapper = styled.div<DepositCardWrapperProps>`
  border: 1px solid #D2DCFF;
  display: flex;
  align-items: center;
  justify-content: ${props => props.noAccount ? "center" : "space-between"};
  flex-direction: row;
  padding: 24px 30px;
  border-radius: 0 0 10px 10px;
  width: 100%;
  max-width: 1110px;
  background: linear-gradient(130.22deg, #9199FF 26.49%, #3C95DB 100.77%);
  box-shadow: inset 0px 4px 4px rgba(0, 0, 0, 0.25);
  color: #fff;
  position: absolute;
  top: 60px;
  z-index: 10;
  height: 90px;

  @media(max-width: 768px) {
    top: 90px;
    height: ${props => props.noAccount ? "100px" : "190px"};
    flex-direction: column; 
  }
`;

const Text = styled.p`
  text-align: left;
  font-size: 17px;
`

const BoldText = styled.p`
  font-size: 18px;
  font-weight: 700;
  text-align: left;
  padding-left: 5px;
`
const SpacerText = styled.p`
  margin-left: 30px;
  margin-right: 30px;
  background: #fff;
  width: 1px;
  height: 18px;
  @media(max-width: 768px) {
    display: none;
  }
`
interface WrapperShadowProps {
    noAccount: boolean
}
const WrapperShadow = styled.div<WrapperShadowProps>`
  background: linear-gradient(130.22deg, #9199FF 26.49%, #3C95DB 100.77%);
  opacity: 0.16;
  border-radius: 10px;
  width: calc(100% - 32px);
  max-width: calc(1110px - 32px);
  height: 50px;
  position: absolute;
  top: 110px;
  z-index: 5;
  @media(max-width: 768px){
    top: ${props => props.noAccount ? "150px" : "240px"};
  }
`

const DepositAmountsWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: start;
  flex-direction: row;
  width: 100%;
  @media(max-width: 768px) {
    flex-direction: column;
    align-items: start;
  }
`

const DepositItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: start;
  flex-direction: row;
  @media(max-width: 768px) {
    margin-bottom: 6px;
  }
`

const ButtonsWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: end;
  flex-direction: row;
  width: 350px;
  @media(max-width: 768px) {
    width: 100%;
    margin-top: 5px;
    justify-content: start;
  }
`
const ButtonsSpacer = styled.div`
  display: none;
  @media(max-width: 768px) {
    display: block;
    margin-left: 20px;
  }
`

interface DepositCardWrapperCompProps {
    totalRobotsDeposit: number
    availableDeposit: number
}
const DepositCardWrapperComp: React.FC<DepositCardWrapperCompProps> = ({totalRobotsDeposit, availableDeposit}) => {

    const {account} = useWeb3React();
    const state = useFarmFromPid(0);
    const busdDeposit = state && state.userData && state.userData.userDeposit && new BigNumber(state.userData.userDeposit) || BIG_ZERO;
    const busdBalance = state && state.userData && state.userData.busdBalance && new BigNumber(state.userData.busdBalance) || BIG_ZERO;

    // deposit
    const [showDeposit, setShowDeposit] = useState(false);
    const [depVal, setDepVal] = useState('');
    const handleChange = useCallback(
        (e: React.FormEvent<HTMLInputElement>) => {
            setDepVal(e.currentTarget.value);
        },
        [setDepVal],
    );
    const fullBalance = getFullDisplayBalance(busdBalance);
    const handleSelectMax = useCallback(() => {
        setDepVal(fullBalance);
    }, [fullBalance]);
    // withdraw
    const [showWithdraw, setShowWithdraw] = useState(false);
    const [withdrawVal, setWithdrawVal] = useState('');
    const handleChangeWithdraw = useCallback(
        (e: React.FormEvent<HTMLInputElement>) => {
            setWithdrawVal(e.currentTarget.value);
        },
        [setWithdrawVal],
    );
    const fullBalWithdraw = getFullDisplayBalance(busdDeposit);
    const handleSelectMaxWithdraw = useCallback(() => {
        setWithdrawVal(fullBalWithdraw);
    }, [fullBalWithdraw]);

    return (
        <div style={{display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column"}}>
            {showDeposit && <DepositModalContract
                setShowDeposit={setShowDeposit}
                handleChange={handleChange}
                handleSelectMax={handleSelectMax}
                val={depVal}
                max={busdBalance}/>}
            {showWithdraw && <WithdrawModalContract
                setShowWithdraw={setShowWithdraw}
                handleChange={handleChangeWithdraw}
                handleSelectMax={handleSelectMaxWithdraw}
                val={withdrawVal}
                max={busdDeposit}/>}

            <DepositCardWrapper noAccount={!account}>
                {account &&
                <DepositAmountsWrapper>
                    <DepositItem>
                        <Text> Deposit:</Text>
                        <BoldText>{correctToFixed((getBalanceNumber(busdDeposit) + totalRobotsDeposit),2)} BUSD</BoldText>
                    </DepositItem>
                    <SpacerText />
                    <DepositItem>
                    <Text> Available:</Text>
                    <BoldText>{correctToFixed(availableDeposit, 2)} BUSD</BoldText>
                    </DepositItem>
                    <SpacerText />
                    <DepositItem>
                    <Text> Active:</Text>
                    <BoldText>{correctToFixed(totalRobotsDeposit, 2)} BUSD</BoldText>
                    </DepositItem>
                </DepositAmountsWrapper>}

                {account &&
                <ButtonsWrapper>
                    <Button appearance={ButtonAppearanceEnum.secondary}
                            onClick={() => setShowDeposit(true)}
                            style={{marginRight: 14}}>
                        <img src={depositImg} alt="" style={{marginRight: 10}}/>
                        <p>Deposit</p>
                    </Button>
                    <ButtonsSpacer />
                    <Button appearance={ButtonAppearanceEnum.secondary}
                            onClick={() => setShowWithdraw(true)}
                    >
                        <img src={withdrawImg} alt="" style={{marginRight: 10}}/>
                        <p style={{color: "#6482E6"}}>Withdraw</p>
                    </Button>
                </ButtonsWrapper>}

                {!account && <p style={{width: "100%", textAlign: "center"}}> Press <span style={{fontWeight: 600}}>Connect Wallet</span> to start</p>}
            </DepositCardWrapper>
            <WrapperShadow noAccount={!account}/>
        </div>
    );
};

export default DepositCardWrapperComp;
